import { PaginationResponse } from '../../Shared/Pagination/PaginationResponse';
import {
  deleteApi,
  getApi,
  postApi,
  putApi,
} from '../../Shared/Services/RestService';
import {
  SupportChannel,
  SupportChannelFilters,
  SupportLink,
  SupportLinkFilters,
  SupportLinkRequest,
  SupportOutreach,
  SupportOutreachRequest,
  SupportOutreachResult,
  SupportVisitor,
  SupportVisitorFilters,
  SupportBulkOutreachRequest,
  SupportBulkOutreachResult,
} from '../Types/SupportType';

const SUPPORT = '/admin/support';

/**
 * Summary cards for the overview. Returns all five channels, including any with no traffic, so
 * the layout does not reflow as data arrives and a quiet channel reads as zero rather than absent.
 */
export const getSupportChannels = (params?: SupportChannelFilters) => {
  return getApi<SupportChannel[]>(`${SUPPORT}/channels`, params);
};

/**
 * Visitor rows for a channel tab.
 *
 * Defaults to `notRegisteredOnly: true` to match the backend default. Opening a channel tab is an
 * intent to find people who arrived and did not finish, so that is what should be shown without
 * the admin having to ask for it.
 */
export const getSupportVisitors = (params?: SupportVisitorFilters) => {
  return getApi<PaginationResponse<SupportVisitor>>(`${SUPPORT}/visitors`, {
    page: 0,
    size: 20,
    notRegisteredOnly: true,
    ...params,
  });
};

/** Campaigns seen on a channel in the window, for the quick-filter dropdown. */
export const getSupportCampaigns = (params?: SupportChannelFilters) => {
  return getApi<string[]>(`${SUPPORT}/campaigns`, params);
};

export const getSupportLinks = (params?: SupportLinkFilters) => {
  return getApi<SupportLink[]>(`${SUPPORT}/links`, params);
};

export const createSupportLink = (payload: SupportLinkRequest) => {
  return postApi<SupportLink>(`${SUPPORT}/links`, payload);
};

export const updateSupportLink = (id: string, payload: SupportLinkRequest) => {
  return putApi<SupportLink>(`${SUPPORT}/links`, id, payload);
};

/**
 * Deletes a link. The backend deliberately keeps its outreach history, so this does not lose the
 * record of what was sent.
 */
export const deleteSupportLink = (id: string) => {
  return deleteApi(`${SUPPORT}/links`, id);
};

/**
 * Records a registration nudge, and sends it when an address is on file and a provider exists.
 *
 * Read `delivered` on the result rather than treating a successful response as confirmation:
 * for an unregistered visitor there is no address, so the common outcome is a stored record plus
 * the composed message for the admin to send themselves.
 */
export const recordSupportOutreach = (payload: SupportOutreachRequest) => {
  return postApi<SupportOutreachResult>(`${SUPPORT}/outreach`, payload);
};

export const getSupportOutreach = (channel?: string | null, page: number = 0, size: number = 20) => {
  const params: Record<string, unknown> = { page, size };
  if (channel) params.channel = channel;
  return getApi<PaginationResponse<SupportOutreach>>(`${SUPPORT}/outreach`, params);
};

/**
 * Resolves a short link slug to its full tracked URL.
 *
 * Public endpoint - the redirect page calls this without an admin session. A 404 for an unknown
 * or archived slug returns `success:false`, which the page reads as "link not found".
 */
export const resolveSupportShortLink = (slug: string) => {
  return getApi<{ url: string }>(`/public/support/r/${encodeURIComponent(slug)}`);
};

/**
 * Resolves an opened share URL - the address the app itself was opened at - to the destination
 * it was mapped to. Public, because it runs before any session exists, once per full page load:
 * a display name such as `http://local-nexgent.tech/` forwards to the tracked URL instead of
 * showing its own path. A miss reads as "not a share URL" and the page stays as it is.
 */
export const resolveShareUrl = (url: string) => {
  return getApi<{ url: string }>(`/public/support/share`, { url });
};

/**
 * Bulk nudge. Either `visitorIds` (explicit rows) or `allMatching` (re-run the current tab
 * filter) must be set. Email tries to send; other channels record each one as shared.
 */
export const recordSupportBulkOutreach = (payload: SupportBulkOutreachRequest) => {
  return postApi<SupportBulkOutreachResult>(`${SUPPORT}/outreach/bulk`, payload);
};

/**
 * The shareable form of a link: the exact string shown under "Ready to share" and the one
 * substituted for `{link}` in an outreach message.
 *
 * <p>A display name that is already a full http(s) URL is returned verbatim, so entering
 * `https://nexgent.tech` shares exactly `https://nexgent.tech` with nothing wrapped around it.
 * Any other display name (a label such as "swapnil") resolves to the short link, which the
 * backend mints from that same display name and which redirects through `{site}/l/{slug}` to
 * the tracked destination, so the full UTM set still lands on the target.</p>
 *
 * <p>Deliberately not markdown: a pasted `[name](url)` shows its brackets in every channel this
 * is shared on, and the admin asked to share one clean value.</p>
 */
export const shareableLink = (link: SupportLink): string => {
  const displayName = (link.displayName || '').trim();
  if (isHttpUrl(displayName)) return displayName;
  return link.shortUrl || link.url || displayName;
};

/** True when the string parses as an absolute http(s) URL, e.g. `https://nexgent.tech` or `http://test`. */
function isHttpUrl(value: string): boolean {
  if (!value) return false;
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * The visitor ids matched by the tab's filter, used to populate the bulk share selection when
 * the admin picks "everyone matching". Distinct across session rows, matching the backend bulk.
 */
export const getSupportVisitorIds = (params?: SupportVisitorFilters) => {
  return getApi<string[]>(`${SUPPORT}/visitor-ids`, {
    page: 0,
    size: 20,
    notRegisteredOnly: true,
    ...params,
  });
};
